
import React, { useState, useMemo, useEffect } from 'react';
import { Brain, Send, X, Terminal, Loader2, Sparkles, Zap, MailCheck, AlertTriangle } from 'lucide-react';
import { geminiService } from '../services/geminiService';
import { emailService } from '../services/emailService';
import { nudgeService } from '../services/nudgeService';
import { User, Task } from '../types';
import { firebaseService } from '../services/firebaseService';

interface Props {
  user: User | null;
  onCommand: (cmd: string) => void;
}

export const AIAgentOverlay: React.FC<Props> = ({ user, onCommand }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);

  useEffect(() => {
    if (user && isOpen) {
      firebaseService.getTasks(user.id).then(setTasks);
    }
  }, [user, isOpen]);

  const activeNudge = useMemo(() => nudgeService.detectActiveNudges(tasks), [tasks]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !user) return;
    setIsProcessing(true);
    
    const cmd = input.toLowerCase();
    
    if (cmd.includes('update') || cmd.includes('notify')) {
      const taskTitle = cmd.split('about')?.[1]?.trim() || "Accountability Update";
      await emailService.dispatchRelay('PULSE', user.email, {
        taskTitle,
        description: "Execution status update."
      });
      setLastAction("Update sent to partners.");
    } else {
      onCommand(input);
      setLastAction("Objective recorded.");
    }
    
    setInput('');
    setIsProcessing(false);
    setTimeout(() => {
      setIsOpen(false);
      setLastAction(null);
    }, 2000);
  };

  return (
    <div className="fixed bottom-6 right-6 z-[200]">
      {isOpen ? (
        <div className={`glass w-80 p-5 rounded-2xl shadow-2xl animate-in slide-in-from-bottom-4 duration-300 border-2 ${activeNudge ? (activeNudge.type === 'URGENT' ? 'border-rose-500/40' : 'border-amber-500/40') : 'border-indigo-500/40'}`}>
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-xs font-mono font-bold text-indigo-400 flex items-center gap-2 tracking-widest uppercase">
              <Zap size={16} className={activeNudge ? (activeNudge.type === 'URGENT' ? 'text-rose-500 fill-rose-500' : 'text-amber-400 fill-amber-400') : 'text-amber-400 fill-amber-400'} /> Execution Assistant
            </h4>
            <button onClick={() => setIsOpen(false)} className="text-slate-500 hover:text-white transition-colors"><X size={18} /></button>
          </div>
          
          {activeNudge && !lastAction && (
            <div className={`mb-4 p-4 rounded-xl border flex items-start gap-3 animate-in fade-in slide-in-from-top-2 ${activeNudge.type === 'URGENT' ? 'bg-rose-500/10 border-rose-500/20 text-rose-600' : 'bg-amber-500/10 border-amber-500/20 text-amber-600'}`}>
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-[10px] font-black uppercase tracking-widest">Tactical Nudge</p>
                <p className="text-[11px] font-bold leading-relaxed italic">"{activeNudge.message}"</p>
              </div>
            </div>
          )}

          {lastAction ? (
            <div className="py-8 flex flex-col items-center justify-center text-center space-y-3 animate-in zoom-in-95">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                <MailCheck size={24} />
              </div>
              <p className="text-sm font-bold text-emerald-400">{lastAction}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <input 
                  autoFocus
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="How can I help you prioritize?"
                  className="w-full bg-black/50 border border-slate-700 rounded-xl py-3 px-4 text-sm text-slate-200 focus:outline-none focus:border-indigo-500 font-mono transition-all"
                />
                <button disabled={isProcessing} className="absolute right-3 top-3 text-indigo-400 hover:text-indigo-300 transition-colors">
                  {isProcessing ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
              <div className="p-3 bg-slate-900/50 rounded-xl border border-slate-800">
                <div className="flex items-center gap-2 text-[10px] text-slate-500 font-mono italic">
                  <Terminal size={12} className="text-indigo-500" /> try "Update about project X"
                </div>
              </div>
            </form>
          )}
        </div>
      ) : (
        <button 
          onClick={() => setIsOpen(true)}
          className={`w-16 h-16 rounded-full shadow-xl flex items-center justify-center text-white transition-all hover:scale-110 active:scale-95 pulse-indigo group relative ${activeNudge ? (activeNudge.type === 'URGENT' ? 'bg-rose-600 shadow-rose-500/40' : 'bg-amber-500 shadow-amber-500/40') : 'bg-indigo-600 shadow-indigo-500/40'}`}
        >
          {activeNudge && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center border-2 border-vanguard-surface shadow-lg z-10">
               <span className={`w-2 h-2 rounded-full animate-ping ${activeNudge.type === 'URGENT' ? 'bg-rose-500' : 'bg-amber-500'}`} />
            </span>
          )}
          <Brain size={32} className={`group-hover:rotate-12 transition-transform ${activeNudge ? (activeNudge.type === 'URGENT' ? 'glow-rose' : 'glow-amber') : ''}`} />
        </button>
      )}
    </div>
  );
};